import { useState, useEffect, useCallback } from "react";
import { json2csv } from "json-2-csv";

import {
  Grid,
  TableCell,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Typography,
  useTheme,
  Tooltip,
  Chip,
  Button,
  Avatar,
  TableContainer,
  TableHead,
  TableBody,
  Table,
  TablePagination,
  Card as MuiCard,
  CardHeader,
  CardContent,
  CardActions
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { faXmark, faPencil } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { sourceDescriptions } from "@aclymatepackages/audit-logs"; // kept for backward compat with pre-migration logs
import {
  ToggleButtons,
  LoadingButton,
  TextField
} from "@aclymatepackages/atoms";
import { editRowData } from "@aclymatepackages/array-immutability-helpers";
import { formatDate } from "@aclymatepackages/formatters";
import { nameToFirstName } from "@aclymatepackages/converters";

import CircularProgressWithLabel from "../../atoms/loading/CircularProgressWithLabel";
import FeatureErrorBoundary from "../../atoms/FeatureErrorBoundary";
import Card from "../../atoms/mui/Card";
import CsvEmailDownloadButton from "../../atoms/buttons/CsvEmailDownloadButton";
import IdDisplayChip from "../../atoms/IdDisplayChip";

import SlideLayout from "../../layouts/SlideLayout";

import SortableTable, {
  makeColumnObj
} from "../../modules/tables/SortableTable";

import WarningPopup from "../../atoms/notifications/WarningPopup";

import { useCompanyAuditLogs } from "../../../helpers/hooks/companyData";
import {
  useCachedDisplayData,
  useCollectionDataListener,
  getTaskFileDownloadUrl,
  useCachedFirebaseCrud,
  useAuth,
  getFileMetadata
} from "../../../helpers/firebase";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import { getAccountId } from "../../../helpers/accountHelpers";
import {
  getFieldOptionsByTaskType,
  getExpectedTypeForField,
  isValueValidForType,
  getTypeErrorMessage,
  getTaskTypesDetails,
  getTaskStatusDetails,
  formatTimestamp,
  getFileNameFromPath,
  transactionsFieldOptions,
  shippingFieldOptions,
  waterCsvFieldOptions,
  steamCsvFieldOptions,
  chillWaterCsvFieldOptions,
  refrigerantsCsvFieldOptions
} from "../../../helpers/components/tasks";
import { bomProductsFieldOptions } from "../../../helpers/components/products";
import { getUrlSearchParams } from "../../../helpers/otherHelpers";

const useHandleDownload = () => {
  const { activateSnackbar } = usePlatformLayoutContext();

  return async ({ filePath, fileBucket }) => {
    const fullFilePath = `${filePath.replace("processed-data", "raw-data")}`;

    try {
      const url = await getTaskFileDownloadUrl(fileBucket, fullFilePath);
      return window.open(url, "_blank");
    } catch (err) {
      activateSnackbar({
        message: "Failed to get download URL.",
        alert: "error"
      });
    }
  };
};

const JsonDisplay = ({ data }) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: palette.backgroundGray.main,
        p: 2,
        borderRadius: 1,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        fontSize: "0.875rem",
        color: palette.text.primary,
        wordBreak: "break-all",
        overflowWrap: "anywhere"
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Box>
  );
};

const AuditLogRow = ({ users, ...log }) => {
  const { timestamp, userId, source, label } = log;
  const { dataBefore, dataAfter, ...generalInfo } = log;
  const { palette } = useTheme();

  const [open, setOpen] = useState(false);

  const getUserDisplay = () => {
    if (!userId || !Array.isArray(users)) {
      return "Unknown";
    }

    const userObj = users.find((u) => u.uid === userId);
    const { name } = userObj || {};

    return name || userId;
  };

  const cellsArray = [
    formatTimestamp(timestamp),
    getUserDisplay(),
    label || sourceDescriptions[source] || "Unknown"
  ];

  return (
    <>
      <TableRow hover>
        {cellsArray.map((value, idx) => (
          <TableCell key={`audit-log-cell-${idx}`}>
            <Typography variant="body2" color={palette.text.primary}>
              {value}
            </Typography>
          </TableCell>
        ))}
        <TableCell align="right">
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            aria-label="expand row"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    General Information
                  </Typography>
                  <JsonDisplay data={generalInfo} />
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    Data Before
                  </Typography>
                  <JsonDisplay data={dataBefore} />
                </Grid>
                <Grid item xs={4}>
                  <Typography
                    variant="h6"
                    color={palette.text.primary}
                    gutterBottom
                  >
                    Data After
                  </Typography>
                  <JsonDisplay data={dataAfter} />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLogTable = ({ auditLogs, error }) => {
  const [employees = []] = useCollectionDataListener("employees");
  const users = employees.map(({ authUid, name, email }) => ({
    uid: authUid,
    name,
    email
  }));

  const rows = Array.isArray(auditLogs)
    ? auditLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    : [];

  const tableColumns = [
    makeColumnObj("TIMESTAMP", "timestamp", true),
    makeColumnObj("USER", "user", true),
    makeColumnObj("SOURCE", "source", true),
    makeColumnObj("", "", false)
  ];

  return error ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography color="error">Failed to load audit logs.</Typography>
    </Box>
  ) : rows.length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography variant="body1">No audit logs found.</Typography>
    </Box>
  ) : (
    <SortableTable
      rows={rows}
      rowComponent={(log) => (
        <AuditLogRow key={`audit-log-${log.id}`} {...log} users={users} />
      )}
      columns={tableColumns}
    />
  );
};

const TaskDocRow = ({ taskDoc, onViewSummary }) => {
  const {
    id: taskDocId,
    startTime,
    type,
    filePath,
    fileBucket,
    status,
    currentChunk,
    totalChunks,
    totalTasks,
    totalTasksProcessed
  } = taskDoc;

  const [user] = useAuth();
  const { displayName, email } = user || {};
  const { palette } = useTheme();
  const [erroredTaskDataRows] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["error", "==", true]]
  );

  const numErroredTaskDataRows = erroredTaskDataRows.filter(
    ({ ignoreInReprocess }) => !ignoreInReprocess
  ).length;
  const isFinishedWithErrors =
    status === "finished" && !!numErroredTaskDataRows;

  const { icon, color, summary } = getTaskStatusDetails(
    taskDoc,
    numErroredTaskDataRows,
    palette
  );
  const {
    Icon,
    color: typeColor,
    name: taskName
  } = getTaskTypesDetails({
    taskType: type,
    palette,
    iconColor: "white"
  });
  const fileName = getFileNameFromPath(filePath, type);

  const onPlaidCsvDownloadBtnClick = async () =>
    await fetchOurApi({
      path: "/transactions/send-plaid-transactions-csv-email",
      method: "POST",
      data: {
        taskDocId,
        email,
        firstName: nameToFirstName(displayName),
        taskDate: formatDate(startTime)
      },
      callback: (res) => res
    });

  const officeCsvTypes = [
    "purchased-water-csv",
    "steam-csv",
    "chill-water-csv",
    "refrigerants-csv"
  ];
  const isOfficeCsvType = officeCsvTypes.includes(type);

  const showRowActionButton = () => {
    if (
      status === "finished" &&
      ((fileBucket && filePath) || isOfficeCsvType)
    ) {
      return (
        <Button
          variant="contained"
          size="small"
          color={isFinishedWithErrors ? "error" : "primary"}
          onClick={onViewSummary}
        >
          View Summary
        </Button>
      );
    }

    if (type === "plaid-transactions") {
      return (
        <CsvEmailDownloadButton
          tooltip="Download bank transactions CSV"
          onDownloadBtnClick={onPlaidCsvDownloadBtnClick}
        />
      );
    }

    return <></>;
  };

  const isActiveTask = status === "uploading" || status === "running";
  const progressPercent =
    isActiveTask && totalChunks
      ? Math.round((currentChunk / totalChunks) * 100)
      : 0;

  return (
    <TableRow>
      <TableCell>
        {isActiveTask ? (
          <Tooltip
            title={
              totalTasks > 0
                ? `${totalTasksProcessed || 0}/${totalTasks} rows`
                : summary
            }
          >
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgressWithLabel value={progressPercent} size={32} />
              {totalTasks > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {totalTasksProcessed || 0}/{totalTasks} rows
                </Typography>
              )}
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={summary}>
            <FontAwesomeIcon
              icon={icon}
              color={color}
              style={{ width: "20px", height: "20px" }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TableCell>{formatTimestamp(startTime)}</TableCell>
      <TableCell>
        <Chip
          style={{
            backgroundColor: typeColor,
            color: "white"
          }}
          label={taskName}
          icon={Icon}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{fileName}</Typography>
      </TableCell>
      <TableCell>
        <IdDisplayChip id={taskDocId} type="task" />
      </TableCell>
      <TableCell>{showRowActionButton()}</TableCell>
    </TableRow>
  );
};

const ErroredTaskDataRow = ({
  row,
  tableHeaders,
  isEditing,
  handleCellEdit,
  handleIgnoreRow,
  setDisplayedRows
}) => {
  const { palette } = useTheme();
  const { ignoreInReprocess, isEdited, displayedRowIndex } = row;
  const editRowDataFunc = editRowData(displayedRowIndex, setDisplayedRows);

  const taskType = row.taskType;
  const fieldOptions = getFieldOptionsByTaskType(taskType);

  const renderStatusCell = (colIndex) => (
    <TableCell
      key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
      align="center"
    >
      {ignoreInReprocess ? (
        <FontAwesomeIcon
          icon={faXmark}
          color={palette.error.main}
          style={{ marginLeft: 8 }}
        />
      ) : isEdited ? (
        <FontAwesomeIcon
          icon={faPencil}
          color={palette.primary.main}
          style={{ marginLeft: 8 }}
        />
      ) : null}
    </TableCell>
  );

  const renderActionsCell = (colIndex) => (
    <TableCell
      key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
      align="right"
    >
      <Tooltip
        title={
          ignoreInReprocess
            ? "Include in reprocessing"
            : "Ignore during reprocessing"
        }
      >
        <IconButton
          onClick={() => handleIgnoreRow(editRowDataFunc, ignoreInReprocess)}
          size="small"
        >
          <DeleteForeverIcon color={ignoreInReprocess ? "error" : "action"} />
        </IconButton>
      </Tooltip>
    </TableCell>
  );

  const getValidationProps = (label, field) => {
    if (!isEditing) {
      return { error: false, helperText: undefined };
    }

    const expectedType = getExpectedTypeForField(fieldOptions, field);
    if (!expectedType) {
      return { error: false, helperText: undefined };
    }

    const isValid = isValueValidForType(row[label], expectedType);
    if (isValid) {
      return { error: false, helperText: undefined };
    }

    return {
      error: true,
      helperText: getTypeErrorMessage(expectedType)
    };
  };

  const renderDataCell = (label, field, align, colIndex) => {
    const { error, helperText } = getValidationProps(label, field);

    const formatCellValue = (value) => {
      if (value instanceof Date) {
        return formatDate(value);
      }
      if (value === null || value === undefined) {
        return "";
      }
      return String(value);
    };

    return (
      <TableCell
        key={`task-data-cell-${displayedRowIndex}-${colIndex}`}
        align={align}
      >
        {isEditing ? (
          <TextField
            value={row[field] ?? ""}
            setValue={(value) => handleCellEdit(editRowDataFunc, label, value)}
            variant="standard"
            fullWidth
            disabled={ignoreInReprocess}
            id={`task-data-cell-text-field-${displayedRowIndex}-${colIndex}-${label}`}
            error={error}
            helperText={helperText}
          />
        ) : (
          formatCellValue(row[field])
        )}
      </TableCell>
    );
  };

  const renderTableRowCell = (label, field, align, colIndex) => {
    if (field === "status") {
      return renderStatusCell(colIndex);
    }

    if (field === "actions") {
      return renderActionsCell(colIndex);
    }

    return renderDataCell(label, field, align, colIndex);
  };

  return (
    <TableRow key={`task-data-row-${displayedRowIndex}`}>
      {tableHeaders.map(({ label, field, align }, colIndex) =>
        renderTableRowCell(label, field, align, colIndex)
      )}
    </TableRow>
  );
};

const TaskDetailSlide = ({ task, onClose }) => {
  const {
    type,
    filePath,
    startTime,
    endTime,
    totalTasks,
    totalTasksProcessed,
    id: taskDocId,
    taskData,
    fileBucket
  } = task;
  const { taskDataColumnNames } = taskData || {};

  const handleDownload = useHandleDownload();
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext();
  const companyId = getAccountId();
  const [erroredTaskDataRows, isErroredTaskDataLoading] = useCachedDisplayData(
    `tasks/${taskDocId}/taskData`,
    [["error", "==", true]]
  );
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isEditing, setIsEditing] = useState(false);
  const [displayedRows, setDisplayedRows] = useState([]);
  const [fileMetadata, setFileMetadata] = useState(null);

  const { Icon, color } = getTaskTypesDetails({
    taskType: type,
    palette,
    fontSize: "medium"
  });
  const fileName = getFileNameFromPath(filePath);
  const date = formatTimestamp(startTime);
  const hasErrors = !isErroredTaskDataLoading && erroredTaskDataRows.length > 0;
  const { summary } = getTaskStatusDetails(task, displayedRows.length, palette);

  const hasModifiedRows = displayedRows.some(
    ({ isEdited, ignoreInReprocess }) => isEdited || ignoreInReprocess
  );

  const statusHeaderObj =
    hasModifiedRows || isEditing
      ? [{ label: "", field: "status", align: "center" }]
      : [];

  const buildTasksDataColumnHeaderObjs = () => {
    const { customMetadata } = fileMetadata || {};

    const extractCsvTypeHeaders = (optionsArray) =>
      optionsArray.map(({ label, value }) => {
        const fieldLabel = customMetadata?.[value] || label || value;
        return { label: fieldLabel, field: value, align: "inherit" };
      });

    const officeCsvFieldOptionsMap = {
      "purchased-water-csv": waterCsvFieldOptions,
      "steam-csv": steamCsvFieldOptions,
      "chill-water-csv": chillWaterCsvFieldOptions,
      "refrigerants-csv": refrigerantsCsvFieldOptions
    };

    if (officeCsvFieldOptionsMap[type]) {
      return extractCsvTypeHeaders(officeCsvFieldOptionsMap[type]);
    }

    if (!fileMetadata) {
      return [];
    }

    if (type === "transactions") {
      return extractCsvTypeHeaders(transactionsFieldOptions);
    }

    if (type === "shipping-transactions") {
      return extractCsvTypeHeaders(shippingFieldOptions);
    }

    if (type === "bom-products") {
      return extractCsvTypeHeaders(bomProductsFieldOptions);
    }

    return Object.entries(taskDataColumnNames || {}).map(
      ([field, displayName]) => ({
        label: displayName,
        field: field,
        align: "inherit"
      })
    );
  };

  const taskDataColumnHeaderObjs = buildTasksDataColumnHeaderObjs();

  const actionsHeaderObj = isEditing
    ? [{ label: "", field: "actions", align: "right" }]
    : [];

  const tableHeaders = [
    ...statusHeaderObj,
    ...taskDataColumnHeaderObjs,
    { label: "Error", field: "errorMessage", align: "inherit" },
    ...actionsHeaderObj
  ];

  const generateDefaultDisplayedRows = useCallback(
    () =>
      erroredTaskDataRows
        .filter(({ ignoreInReprocess }) => !ignoreInReprocess)
        .map((row, idx) => ({
          ...row,
          displayedRowIndex: idx,
          isEdited: false,
          ignoreInReprocess: false,
          taskType: type
        })),
    [erroredTaskDataRows, type]
  );

  useEffect(() => {
    if (erroredTaskDataRows) {
      setDisplayedRows(generateDefaultDisplayedRows());
    }
  }, [erroredTaskDataRows, generateDefaultDisplayedRows]);

  useEffect(() => {
    if (filePath && !fileMetadata) {
      getFileMetadata(filePath, fileBucket).then(setFileMetadata);
    }
  }, [filePath, fileMetadata, fileBucket]);

  const handleArchiveTask = () => {
    setIsArchiving(true);

    return fetchOurApi({
      path: "/transactions/archive-task-and-task-transactions",
      method: "POST",
      data: {
        companyId,
        taskDocId
      },
      callback: () => {
        activateSnackbar({
          message:
            "The process and it's associated transactions have been deleted.",
          alert: "success"
        });

        return onClose();
      },
      setError: () => {
        activateSnackbar({
          message: "Failed to archive process and transactions.",
          alert: "error"
        });

        return setIsArchiving(false);
      }
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    return setPage(0);
  };

  const handleCellEdit = (editRowDataFunc, columnName, value) => {
    editRowDataFunc(columnName, value);

    return editRowDataFunc("isEdited", true);
  };

  const handleIgnoreRow = (editRowDataFunc, currentIgnoreInReprocess) =>
    editRowDataFunc("ignoreInReprocess", !currentIgnoreInReprocess);

  const handleResetChanges = () =>
    setDisplayedRows(generateDefaultDisplayedRows());

  const handleReprocessErrorRows = async () => {
    setIsSaving(true);

    try {
      const modifiedRows = displayedRows.filter(
        (row) => row.isEdited || row.ignoreInReprocess
      );

      await Promise.all(
        modifiedRows.map(
          ({ id, isEdited, ignoreInReprocess, ...otherRowData }) =>
            updateCollectionDoc(
              `tasks/${taskDocId}/taskData`,
              id,
              {
                ...otherRowData,
                isEdited,
                ignoreInReprocess
              }
            )
        )
      );

      await updateCollectionDoc(
        "tasks",
        taskDocId,
        {
          isReprocessing: true,
          currentChunk: 0,
          status: "running"
        }
      );

      activateSnackbar({
        message: "The task is being reprocessed with your modifications.",
        alert: "success"
      });

      onClose();
    } catch (error) {
      console.error("Error saving changes:", error);

      activateSnackbar({
        message: "Failed to save changes. Please try again.",
        alert: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const paginatedRows = displayedRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDownloadErrorRowsCsv = () => {
    try {
      const downloadErrorRows = erroredTaskDataRows.filter(
        ({ ignoreInReprocess }) => !ignoreInReprocess
      );
      const csv = json2csv(downloadErrorRows);

      const csvBlob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `error-rows-${fileName}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      activateSnackbar({
        message: "Failed to download error rows CSV.",
        alert: "error"
      });
    }
  };

  return (
    <SlideLayout
      isSlideOpen={!!task}
      setIsSlideOpen={onClose}
      header={
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Grid container justifyContent="center" alignItems="center">
              <Avatar style={{ backgroundColor: color }}>{Icon}</Avatar>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container direction="column">
              <Grid item>
                <Typography variant="body1">{`${date} - ${fileName}`}</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2">{summary}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      }
      content={
        <MuiCard style={{ backgroundColor: palette.backgroundGray.main }}>
          {isErroredTaskDataLoading ? (
            <CardContent>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                p={2}
              >
                <Typography variant="body1">Loading task data...</Typography>
              </Box>
            </CardContent>
          ) : hasErrors ? (
            <>
              <CardHeader
                title="Emissions To Fix"
                action={
                  <Grid container spacing={1} alignItems="center">
                    <Grid item>
                      <Tooltip title="Download Error Rows CSV">
                        <IconButton
                          onClick={handleDownloadErrorRowsCsv}
                          size="large"
                        >
                          <FileDownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip title="Reset Changes">
                        <IconButton onClick={handleResetChanges} size="large">
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item>
                      <Tooltip title={isEditing ? "Save Changes" : "Edit Rows"}>
                        <IconButton
                          onClick={() => setIsEditing(!isEditing)}
                          size="large"
                        >
                          {isEditing ? <SaveIcon /> : <EditIcon />}
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map(({ label, align }, idx) => (
                          <TableCell
                            key={`task-data-header-${idx}`}
                            align={align}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedRows.map((row, rowIndex) => (
                        <ErroredTaskDataRow
                          key={`task-data-row-${rowIndex}`}
                          row={row}
                          tableHeaders={tableHeaders}
                          isEditing={isEditing}
                          handleCellEdit={handleCellEdit}
                          handleIgnoreRow={handleIgnoreRow}
                          setDisplayedRows={setDisplayedRows}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box display="flex" justifyContent="flex-end">
                  <TablePagination
                    component="div"
                    count={displayedRows.length}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </Box>
              </CardContent>
              {hasModifiedRows && !isEditing && (
                <CardActions style={{ justifyContent: "center" }}>
                  <LoadingButton
                    isLoading={isSaving}
                    label="Reprocess Error Rows"
                    color="primary"
                    onClick={handleReprocessErrorRows}
                  />
                </CardActions>
              )}
            </>
          ) : (
            <>
              <CardHeader title="Upload Summary" />
              <CardContent>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      Start Time
                    </Typography>
                    <Typography variant="body1">
                      {formatTimestamp(startTime)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      End Time
                    </Typography>
                    <Typography variant="body1">
                      {endTime ? formatTimestamp(endTime) : "—"}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="textSecondary">
                      Rows Processed
                    </Typography>
                    <Typography variant="body1">
                      {totalTasksProcessed ?? 0} / {totalTasks ?? 0}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </>
          )}
        </MuiCard>
      }
      footer={
        <Grid container direction="column" spacing={2}>
          {showDeleteWarning && (
            <Grid item>
              <WarningPopup
                warningTitle="Are you sure you would like to delete this process and all transactions associated with it? This action cannot be undone."
                warningInput={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <LoadingButton
                      isLoading={isArchiving}
                      label="Yes, I would like to delete"
                      onClick={handleArchiveTask}
                    />
                  </Box>
                }
                backgroundColor={palette.error.main}
              />
            </Grid>
          )}
          <Grid
            item
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item>
              <Tooltip title="Delete this process and all transactions associated with it">
                <span>
                  <IconButton
                    size="large"
                    onClick={() => setShowDeleteWarning((prev) => !prev)}
                    disabled={isArchiving}
                  >
                    <DeleteForeverIcon color="error" />
                  </IconButton>
                </span>
              </Tooltip>
            </Grid>
            {filePath && fileBucket && (
              <Grid item>
                <LoadingButton
                  isLoading={false}
                  label="Download Full CSV"
                  color="primary"
                  disabled={false}
                  onClick={() => handleDownload(task)}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      }
    />
  );
};

const ProcessLogTable = ({ taskDocs, error }) => {
  const [selectedTask, setSelectedTask] = useState(null);

  const filteredAndSortedTaskDocs = taskDocs
    .filter(({ archived }) => !archived)
    .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  const processTableColumns = [
    makeColumnObj("STATUS", "status", true),
    makeColumnObj("START TIME", "startTime", true),
    makeColumnObj("TYPE", "type", true),
    makeColumnObj("FILE NAME", "fileName", true),
    makeColumnObj("TASK ID", "id", true),
    makeColumnObj("ACTION", "action", false)
  ];

  return error ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography color="error">Failed to load process logs.</Typography>
    </Box>
  ) : filteredAndSortedTaskDocs.length === 0 ? (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      <Typography variant="body1">No process logs found.</Typography>
    </Box>
  ) : (
    <>
      <SortableTable
        rows={filteredAndSortedTaskDocs}
        rowComponent={(taskDoc) => (
          <TaskDocRow
            key={`task-doc-${taskDoc.id}`}
            taskDoc={taskDoc}
            onViewSummary={() => setSelectedTask(taskDoc)}
          />
        )}
        columns={processTableColumns}
      />
      {selectedTask && (
        <TaskDetailSlide
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </>
  );
};

const AccountActivity = () => {
  const [auditLogs, isAuditLogsLoading, error] = useCompanyAuditLogs();
  const [taskDocs, taskDocsLoading] = useCollectionDataListener("tasks");

  const selectedView = getUrlSearchParams("view");

  const [selectedTab, setSelectedTab] = useState(
    selectedView === "audit-logs" ? "audit-logs" : "process-logs"
  );

  return (
    <FeatureErrorBoundary featureName="Account Activity">
      <Card
        cardType="layout"
        gridProps={{ sm: 12 }}
        title={selectedTab === "audit-logs" ? "Audit Logs" : "Process Logs"}
        subtitle="Track all changes and processing tasks performed in your account"
        icon={<HistoryIcon />}
        isLoading={
          selectedTab === "audit-logs" ? isAuditLogsLoading : taskDocsLoading
        }
        action={
          <ToggleButtons
            value={selectedTab}
            onChange={setSelectedTab}
            buttons={[
              {
                value: "process-logs",
                name: "Process Logs",
                id: "process-logs-toggle"
              },
              {
                value: "audit-logs",
                name: "Audit Logs",
                id: "audit-logs-toggle"
              }
            ]}
          />
        }
      >
        {selectedTab === "audit-logs" ? (
          <AuditLogTable auditLogs={auditLogs} error={error} />
        ) : (
          <ProcessLogTable taskDocs={taskDocs} error={error} />
        )}
      </Card>
    </FeatureErrorBoundary>
  );
};

export default AccountActivity;
